import React from "react";
import PendingReviewBadge from "./PendingReviewBadge";

/**
 * Pending-review badge for a chip / name that surfaces an entity-shaped
 * record with two independent staging signals (issue #9728):
 *
 *   - `staging`       — the record itself is AI-created and not yet
 *                        approved.
 *   - `link_staging`  — the *link* from this surface to the record (an
 *                        interaction participant row, a fact cell, a
 *                        backreference fact) is AI-suggested and not yet
 *                        approved.
 *
 * Renders nothing when both are false. Otherwise renders a single
 * `PendingReviewBadge` with the tooltip wording picked from the
 * three-branch matrix the spec mandates (.claude/features/9728-linked-entity-staging.md).
 *
 * Shared by every entity chip / name on the RMS surface so the wording
 * is centralised. Call sites pass the underlying record's `name` (which
 * the badge interpolates into the tooltip) plus the two flags — they
 * don't compose tooltip text themselves. If you're rendering a pending
 * badge for a non-entity row (interactions, documents, fields), use
 * the bare `PendingReviewBadge` with a hand-written tooltip instead.
 */
export interface LinkedEntityPendingReviewBadgeProps {
  entity:
    | {
        name: string;
        display_name?: string | null;
        staging?: boolean;
        link_staging?: boolean;
      }
    | null
    | undefined;
  /** Pixel size of the clock icon. Passed through to PendingReviewBadge. */
  size?: number;
}

export function linkedEntityPendingReviewTooltip(entity: {
  name: string;
  display_name?: string | null;
  staging?: boolean;
  link_staging?: boolean;
}): string | null {
  const entityStaging = !!entity.staging;
  const linkStaging = !!entity.link_staging;
  if (!entityStaging && !linkStaging) return null;
  const label = entity.display_name || entity.name;
  if (entityStaging && linkStaging) {
    return `Pending review — ${label} was AI-created and the reference to it here is AI-suggested. Neither has been approved yet.`;
  }
  if (entityStaging) {
    return `Pending review — ${label} was AI-created and hasn't been approved yet.`;
  }
  return `Pending review — the reference to ${label} here was AI-suggested and hasn't been approved yet.`;
}

const LinkedEntityPendingReviewBadge: React.FC<
  LinkedEntityPendingReviewBadgeProps
> = ({ entity, size }) => {
  if (!entity) return null;
  const tooltip = linkedEntityPendingReviewTooltip(entity);
  if (tooltip === null) return null;
  return (
    <PendingReviewBadge
      staging={true}
      tooltip={tooltip}
      {...(size !== undefined ? { size } : {})}
    />
  );
};

export default LinkedEntityPendingReviewBadge;
